import { useEffect, useRef, useState } from 'react';

import type { AgentMessage } from '../types';
import { extractWorkflowRecommendations } from './utils';
import type { WorkflowSuggestion } from './WorkflowRoutingAgent';

interface UseWorkflowRecommendationsProps {
  messages: AgentMessage[];
  streamMessage: AgentMessage | null;
  thinkingEvents: string[];
  isStreaming: boolean;
  isSendingMessage: boolean;
  fallbackResponse?: string;
}

export const useWorkflowRecommendations = ({
  messages,
  streamMessage,
  thinkingEvents,
  isStreaming,
  isSendingMessage,
  fallbackResponse,
}: UseWorkflowRecommendationsProps) => {
  const [workflows, setWorkflows] = useState<WorkflowSuggestion[] | null>(null);
  const [storedThinkingEvents, setStoredThinkingEvents] = useState<string[]>(
    [],
  );
  const [shouldShowFallback, setShouldShowFallback] = useState(false);
  const wasStreamingRef = useRef(false);
  const workflowsRef = useRef<WorkflowSuggestion[] | null>(null);
  workflowsRef.current = workflows;

  useEffect(() => {
    if (isStreaming || isSendingMessage) {
      wasStreamingRef.current = true;
    }

    const extractedWorkflows = streamMessage
      ? extractWorkflowRecommendations(streamMessage)
      : [];

    if (thinkingEvents.length > 0) {
      setStoredThinkingEvents(thinkingEvents);
    } else if (!isStreaming && !isSendingMessage) {
      setStoredThinkingEvents([]);
    }

    if (extractedWorkflows.length > 0) {
      setWorkflows(extractedWorkflows);
      setShouldShowFallback(false);
      wasStreamingRef.current = false;
      return;
    }

    const streamHasEnded = !isStreaming && !isSendingMessage;
    const hasNoWorkflows = extractedWorkflows.length === 0;

    if (streamHasEnded && hasNoWorkflows && wasStreamingRef.current) {
      setShouldShowFallback(true);
      setWorkflows(null);
      wasStreamingRef.current = false;
      return;
    }

    const shouldClearWorkflows =
      (!workflowsRef.current || workflowsRef.current.length === 0) &&
      !isStreaming &&
      !isSendingMessage &&
      !streamMessage;

    if (shouldClearWorkflows) {
      setWorkflows(null);
      setShouldShowFallback(false);
      wasStreamingRef.current = false;
    }
  }, [messages, streamMessage, thinkingEvents, isStreaming, isSendingMessage]);

  const fallbackMessage = shouldShowFallback ? fallbackResponse : undefined;

  return {
    workflows,
    storedThinkingEvents,
    fallbackMessage,
  };
};
